export enum BreadcrumbSegmentKind {
  ELLIPSIS = 'ellipsis',
  ITEM = 'item',
}

export type BreadcrumbTrailItem = {
  id: string;
  label: string;
};

export type BreadcrumbTrailSegment =
  | {
      item: BreadcrumbTrailItem;
      kind: BreadcrumbSegmentKind.ITEM;
    }
  | {
      items: BreadcrumbTrailItem[];
      kind: BreadcrumbSegmentKind.ELLIPSIS;
    };

const itemSegment = (item: BreadcrumbTrailItem): BreadcrumbTrailSegment => ({
  item,
  kind: BreadcrumbSegmentKind.ITEM,
});

/** `gap-2` on the list, which sits on both sides of every separator. */
const TRAIL_GAP = 8;

/** The root, and the two crumbs the reader is standing in, are the trail's irreducible minimum. */
const MIN_TAIL = 2;

export type BreadcrumbFitMetrics = {
  /** The width the row has to lay itself out in. */
  available: number;
  ellipsisWidth: number;
  /** Every crumb's natural width, root first. */
  itemWidths: number[];
  separatorWidth: number;
};

/**
 * How many crumbs the row can show at the width it has, for `collapseBreadcrumbItems` to fold
 * the rest away.
 *
 * Collapsing is about space, not depth: a wide screen with short folder names shows the whole
 * path however deep it runs, and a narrow one folds the middle away at the first crumb that does
 * not fit. Counting crumbs instead hid folders from people who had room for every one of them.
 *
 * Nothing here truncates: a trail that does not fit even at the minimum is left to the row's own
 * ellipses, which take from the crumbs behind before they touch the one the reader is in.
 */
export const fittingMaxVisible = ({
  available,
  ellipsisWidth,
  itemWidths,
  separatorWidth,
}: BreadcrumbFitMetrics): number => {
  const count = itemWidths.length;

  if (count === 0) return 0;

  const sum = (widths: number[]) => widths.reduce((total, width) => total + width, 0);
  // One separator and the gap on either side of it: what every join between two crumbs costs.
  const join = separatorWidth + TRAIL_GAP * 2;

  if (sum(itemWidths) + (count - 1) * join <= available) return count;

  const head = itemWidths[0] ?? 0;

  for (let tail = count - 2; tail > MIN_TAIL; tail -= 1) {
    const width = head + ellipsisWidth + sum(itemWidths.slice(count - tail)) + (tail + 1) * join;

    if (width <= available) return tail + 1;
  }

  return MIN_TAIL + 1;
};

// A short trail stays intact. A longer one keeps the root and the tail, and
// folds the middle into one menu so the row does not wrap.
export const collapseBreadcrumbItems = (
  items: BreadcrumbTrailItem[],
  maxVisible = 4,
): BreadcrumbTrailSegment[] => {
  if (items.length <= maxVisible) {
    return items.map(itemSegment);
  }

  const tailCount = maxVisible - 1;
  const head = items[0];
  const tail = items.slice(items.length - tailCount);
  const hidden = items.slice(1, items.length - tailCount);

  return [
    itemSegment(head),
    { items: hidden, kind: BreadcrumbSegmentKind.ELLIPSIS },
    ...tail.map(itemSegment),
  ];
};
